import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getUserLikes, toggleLike } from '@/services/likes.api';

export interface LikedVideo {
  video: {
    id: string;
    videoId: string;
    title: string;
    description?: string;
    thumbnailUrl?: string;
    publishedAt: string;
    channelId: {
      id: string;
      channelId: string;
      name: string;
      thumbnailUrl?: string;
    };
  };
  likedAt: string;
}

export interface CollectionStats {
  totalLikes: number;
  mostLikedGenres: Array<{
    genre: string;
    count: number;
  }>;
  recentActivity: {
    thisWeek: number;
    thisMonth: number;
  };
}

export interface CollectionsState {
  likedVideos: LikedVideo[];
  likedVideosLoading: boolean;
  likedVideosError: string | null;
  likedVideosPagination: {
    offset: number;
    limit: number;
    hasMore: boolean;
  };

  stats: CollectionStats | null;
  statsLoading: boolean;
  statsError: string | null;

  activeCollection: 'liked' | 'playlists' | 'recent' | 'genres';
  selectedVideos: string[];
}

const initialState: CollectionsState = {
  likedVideos: [],
  likedVideosLoading: false,
  likedVideosError: null,
  likedVideosPagination: {
    offset: 0,
    limit: 20,
    hasMore: false
  },

  stats: null,
  statsLoading: false,
  statsError: null,

  activeCollection: 'liked',
  selectedVideos: []
};

export const fetchLikedVideos = createAsyncThunk(
  'collections/fetchLikedVideos',
  async ({ limit = 20, offset = 0, reset = false }: { 
    limit?: number; 
    offset?: number; 
    reset?: boolean;
  } = {}) => {
    const response = await getUserLikes({ limit, offset });
    return {
      videos: response.likes,
      pagination: response.pagination,
      reset
    };
  }
);

export const removeLikedVideo = createAsyncThunk(
  'collections/removeLikedVideo',
  async (videoId: string) => {
    await toggleLike(videoId);
    return videoId;
  }
);

const collectionsSlice = createSlice({
  name: 'collections',
  initialState,
  reducers: {
    setActiveCollection: (state, action: PayloadAction<CollectionsState['activeCollection']>) => {
      state.activeCollection = action.payload;
    },

    setSelectedVideos: (state, action: PayloadAction<string[]>) => {
      state.selectedVideos = action.payload;
    },

    toggleVideoSelection: (state, action: PayloadAction<string>) => {
      const videoId = action.payload;
      if (state.selectedVideos.includes(videoId)) {
        state.selectedVideos = state.selectedVideos.filter(id => id !== videoId);
      } else {
        state.selectedVideos.push(videoId);
      }
    },

    clearSelectedVideos: (state) => {
      state.selectedVideos = [];
    },

    clearLikedVideosError: (state) => {
      state.likedVideosError = null;
    },

    clearStatsError: (state) => {
      state.statsError = null;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchLikedVideos.pending, (state) => {
        state.likedVideosLoading = true;
        state.likedVideosError = null;
      })
      .addCase(fetchLikedVideos.fulfilled, (state, action) => {
        state.likedVideosLoading = false;
        const { videos, pagination, reset } = action.payload;

        if (reset) {
          state.likedVideos = videos as any;
        } else {
          const seen = new Set(state.likedVideos.map((item: any) => item.video?.videoId ?? item.videoId));
          state.likedVideos = [
            ...state.likedVideos,
            ...(videos as any[]).filter((item: any) => !seen.has(item.video?.videoId ?? item.videoId)),
          ] as any;
        }

        state.likedVideosPagination = {
          offset: pagination.offset + videos.length,
          limit: pagination.limit,
          hasMore: pagination.hasMore
        };
      })
      .addCase(fetchLikedVideos.rejected, (state, action) => {
        state.likedVideosLoading = false;
        state.likedVideosError = action.error.message || 'Failed to fetch liked videos';
      });

    builder
      .addCase(removeLikedVideo.fulfilled, (state, action) => {
        const videoId = action.payload;
        state.likedVideos = state.likedVideos.filter(item => item.video.id !== videoId);
        state.selectedVideos = state.selectedVideos.filter(id => id !== videoId);
      });
  }
});

export const selectLikedVideos = (state: { collections: CollectionsState }) => 
  state.collections.likedVideos;

export const selectLikedVideosLoading = (state: { collections: CollectionsState }) =>
  state.collections.likedVideosLoading;

export const selectLikedVideosError = (state: { collections: CollectionsState }) =>
  state.collections.likedVideosError;

export const selectLikedVideosPagination = (state: { collections: CollectionsState }) =>
  state.collections.likedVideosPagination;

export const selectCollectionStats = (state: { collections: CollectionsState }) =>
  state.collections.stats;

export const selectActiveCollection = (state: { collections: CollectionsState }) =>
  state.collections.activeCollection;

export const selectSelectedVideos = (state: { collections: CollectionsState }) =>
  state.collections.selectedVideos;

export const {
  setActiveCollection,
  setSelectedVideos,
  toggleVideoSelection,
  clearSelectedVideos,
  clearLikedVideosError,
  clearStatsError
} = collectionsSlice.actions;

export default collectionsSlice.reducer;
